import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Autocomplete,
  Button,
  IconButton,
  InputAdornment,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert,
  Grid,
  Snackbar,
  Chip,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Stack,
  Avatar,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import ImageIcon from "@mui/icons-material/Image";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SidebarMenu from "./AdminPanel/SidebarMenu";
import TopBar from "./AdminPanel/TopBar";
import { useNavigate } from "react-router-dom";

function getCSRFToken() {
  const name = "csrftoken";
  const m = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return m ? decodeURIComponent(m[2]) : null;
}

const IVA_OPTIONS = [0, 5, 15, 999];
const TIPOS = ["Color", "Capacidad", "Medida", "Tamaño", "Presentación", "Material"];

export default function Variant() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);

  const [productos, setProductos] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [openAuto, setOpenAuto] = useState(false);
  const [producto, setProducto] = useState(null);

  const [tipo, setTipo] = useState("");
  const [valor, setValor] = useState("");
  const [precio, setPrecio] = useState("");
  const [ivaPorc, setIvaPorc] = useState(15);
  const [stockMin, setStockMin] = useState("");
  const [imagen, setImagen] = useState(null);

  const [variantes, setVariantes] = useState([]);
  const [variantesExistentes, setVariantesExistentes] = useState([]);
  const [tipoPermitido, setTipoPermitido] = useState("");

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [saving, setSaving] = useState(false);

  const ivaValorPreview = useMemo(() => {
    const p = parseFloat(precio || "0");
    const porc = parseInt(ivaPorc || 0, 10);
    if (!Number.isFinite(p) || porc === 999) return "0.00";
    return (Math.round(p * (porc / 100) * 100) / 100).toFixed(2);
  }, [precio, ivaPorc]);

  const activeStep = useMemo(() => {
    if (!producto) return 0;
    if (!variantes.length) return 1;
    return 2;
  }, [producto, variantes.length]);

  const resumen = useMemo(() => {
    const total = variantes.length;
    const promedio =
      total > 0 ? (variantes.reduce((acc, v) => acc + Number(v.precio || 0), 0) / total).toFixed(2) : "0.00";
    const conImagen = variantes.filter((v) => !!v.imagen).length;
    return { total, promedio, conImagen };
  }, [variantes]);

  useEffect(() => {
    if (busqueda.length < 2 || producto) return;
    let active = true;
    setLoadingSearch(true);
    (async () => {
      try {
        const resp = await fetch(`/proformas/buscar-productos/?q=${encodeURIComponent(busqueda)}`, {
          credentials: "include",
        });
        const { results = [] } = await resp.json();
        if (active) setProductos(results);
      } catch {
        if (active) setProductos([]);
      } finally {
        if (active) setLoadingSearch(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [busqueda, producto]);

  const cargarVariantes = async (code) => {
    try {
      const res = await fetch(`/productos/ver-variantes/?code=${encodeURIComponent(code)}`, {
        credentials: "include",
      });
      const { variants = [] } = await res.json();
      setVariantesExistentes(variants);
      const firstType = variants.length ? variants[0].variant_type : "";
      setTipoPermitido(firstType);
      setTipo(firstType || "");
    } catch {
      setVariantesExistentes([]);
      setTipoPermitido("");
      setTipo("");
    }
  };

  const onSelect = async (p) => {
    if (!p) return;
    setProducto(p);
    setBusqueda(`${p.name} (${p.code})`);
    setOpenAuto(false);
    setVariantes([]);
    setTipo("");
    setValor("");
    setPrecio("");
    setIvaPorc(15);
    setStockMin("");
    setImagen(null);
    await cargarVariantes(p.code);
  };

  const clearProduct = () => {
    setProducto(null);
    setBusqueda("");
    setProductos([]);
    setOpenAuto(false);
    setVariantesExistentes([]);
    setVariantes([]);
    setTipoPermitido("");
    setTipo("");
    setValor("");
    setPrecio("");
    setIvaPorc(15);
    setStockMin("");
    setImagen(null);
    setError("");
  };

  const clearFields = () => {
    setValor("");
    setPrecio("");
    setIvaPorc(tipoPermitido && variantesExistentes[0]?.iva_porcentaje ? variantesExistentes[0].iva_porcentaje : 15);
    setStockMin("");
    setImagen(null);
    setError("");
  };

  const validateVariant = () => {
    if (!producto) {
      setError("Primero selecciona un producto.");
      return false;
    }
    if (!tipo) {
      setError("Selecciona el tipo de variante.");
      return false;
    }
    if (!valor.trim()) {
      setError("Escribe el valor de la variante.");
      return false;
    }
    if (!precio || Number(precio) <= 0) {
      setError("Ingresa un precio válido.");
      return false;
    }
    if (tipoPermitido && tipo !== tipoPermitido) {
      setError(`Este producto solo permite variantes del tipo "${tipoPermitido}".`);
      return false;
    }
    if (!tipoPermitido && variantesExistentes.length > 0 && tipo !== variantesExistentes[0].variant_type) {
      setError(`Este producto ya tiene variantes del tipo "${variantesExistentes[0].variant_type}".`);
      return false;
    }
    if (!tipoPermitido && variantes.length > 0 && tipo !== variantes[0].tipo) {
      setError(`En esta tanda solo puedes usar el tipo "${variantes[0].tipo}".`);
      return false;
    }
    if (
      variantes.some(
        (v) =>
          v.tipo.toLowerCase() === tipo.toLowerCase() &&
          v.valor.trim().toLowerCase() === valor.trim().toLowerCase()
      )
    ) {
      setError("Ya agregaste una variante con ese valor en esta tanda.");
      return false;
    }
    if (
      variantesExistentes.some(
        (v) =>
          v.variant_type.toLowerCase() === tipo.toLowerCase() &&
          v.value.trim().toLowerCase() === valor.trim().toLowerCase()
      )
    ) {
      setError("Ese valor ya existe en las variantes guardadas.");
      return false;
    }
    return true;
  };

  const addVariant = () => {
    if (!validateVariant()) return;

    setVariantes((v) => [
      ...v,
      {
        id: Date.now(),
        tipo,
        valor: valor.trim(),
        precio: +precio,
        iva_porcentaje: ivaPorc,
        iva_valor: parseFloat(ivaValorPreview),
        stock_minimo: stockMin === "" ? null : +stockMin,
        imagen,
      },
    ]);

    if (!tipoPermitido) setTipoPermitido(tipo);
    clearFields();
    setSuccessMessage("Variante añadida a la lista");
  };

  const deleteVariant = (id) => {
    const next = variantes.filter((x) => x.id !== id);
    setVariantes(next);
    if (next.length === 0 && variantesExistentes.length === 0) {
      setTipoPermitido("");
      setTipo("");
    }
  };

  const saveAll = async () => {
    if (!producto) {
      setError("Selecciona un producto.");
      return;
    }
    if (!variantes.length) {
      setError("No hay variantes para guardar.");
      return;
    }

    const csrfToken = getCSRFToken();
    const formData = new FormData();
    formData.append("product_id", producto.id);

    variantes.forEach((v, i) => {
      formData.append(`variants[${i}][variant_type]`, v.tipo);
      formData.append(`variants[${i}][value]`, v.valor);
      formData.append(`variants[${i}][price]`, v.precio);
      formData.append(`variants[${i}][iva_porcentaje]`, v.iva_porcentaje);
      if (v.stock_minimo !== null && v.stock_minimo !== undefined) {
        formData.append(`variants[${i}][stock_minimo]`, v.stock_minimo);
      }
      if (v.imagen) {
        formData.append(`variants[${i}][image]`, v.imagen);
      }
    });

    try {
      setSaving(true);
      const resp = await fetch("/productos/crear-variantes/", {
        method: "POST",
        credentials: "include",
        headers: { "X-CSRFToken": csrfToken },
        body: formData,
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || "Error al guardar variantes");
      setSuccessMessage("Variantes guardadas exitosamente");
      setVariantes([]);
      await cargarVariantes(producto.code);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch("/sistema/logout/", { method: "POST", credentials: "include" });
    } catch {}
    localStorage.clear();
    navigate("/login");
  };

  const handleSelect = (component) => {
    if (component === "AdminSection") navigate("/admin-panel/secciones");
    else if (component === "ModalInventario") setSubmenuOpen((s) => !s);
    else if (component === "AdminProduct") navigate("/admin-panel/inventario/productos");
    else if (component === "AdminVariant") navigate("/admin-panel/inventario/variantes");
    else if (component === "AdminProforma") navigate("/admin-panel/proformas");
    else if (component === "AdminClient") navigate("/admin-panel/clientes");
    else if (component === "AdminPromotion") navigate("/admin-panel/promociones");
    else navigate("/admin-panel");
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#eef3f8" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        navigate={navigate}
        handleLogout={handleLogout}
      />
      <Box sx={{ flexGrow: 1 }}>
        <TopBar open={open} setOpen={setOpen} onSelect={handleSelect} />
        <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, gap: 2, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              sx={{
                textTransform: "none",
                bgcolor: "#1565C0",
                "&:hover": { bgcolor: "#0D47A1" },
                borderRadius: 2,
                px: 2.2,
                fontWeight: 700,
                boxShadow: "0 8px 20px rgba(21,101,192,0.22)",
              }}
              onClick={() => navigate(-1)}
            >
              Regresar
            </Button>
          </Box>

          <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 4, boxShadow: "0 10px 35px rgba(15,23,42,0.08)" }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" fontWeight={800} sx={{ color: "#1565C0", mb: 0.5 }}>
                Asistente de variantes
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b" }}>
                Primero elige un producto, luego define el tipo de variante y finalmente agrega todos los valores que necesites antes de guardar.
              </Typography>
            </Box>

            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
              <Step>
                <StepLabel>Seleccionar producto</StepLabel>
              </Step>
              <Step>
                <StepLabel>Agregar valores</StepLabel>
              </Step>
              <Step>
                <StepLabel>Guardar cambios</StepLabel>
              </Step>
            </Stepper>

            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
                {error}
              </Alert>
            )}

            <Grid container spacing={2.5} sx={{ mb: 3 }}>
              <Grid item xs={12} md={8}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 4,
                    borderColor: "#dbe6f3",
                    bgcolor: "#f8fbff",
                    height: "100%",
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                    <Avatar sx={{ bgcolor: "#dbeafe", color: "#1565C0" }}>
                      <SearchIcon />
                    </Avatar>
                    <Box>
                      <Typography fontWeight={800}>Paso 1: busca el producto</Typography>
                      <Typography variant="body2" sx={{ color: "#64748b" }}>
                        Escribe al menos 2 letras del nombre o el código.
                      </Typography>
                    </Box>
                  </Stack>

                  <Autocomplete
                    options={productos}
                    getOptionLabel={(o) => `${o.name} (${o.code})`}
                    inputValue={busqueda}
                    open={openAuto}
                    loading={loadingSearch}
                    onOpen={() => busqueda.length >= 2 && setOpenAuto(true)}
                    onClose={() => setOpenAuto(false)}
                    onInputChange={(_, v, reason) => {
                      if (reason === "input") {
                        setBusqueda(v);
                        setProducto(null);
                        setOpenAuto(v.length >= 2);
                      }
                    }}
                    onChange={(_, v) => onSelect(v)}
                    noOptionsText="No hay resultados"
                    freeSolo
                    disableClearable
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Buscar producto por nombre o código"
                        fullWidth
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ color: "#64748b" }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={clearProduct} edge="end">
                                <CloseIcon />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 4,
                    borderColor: "#dbe6f3",
                    bgcolor: "#f8fbff",
                    height: "100%",
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                    <Avatar sx={{ bgcolor: "#dcfce7", color: "#15803d" }}>
                      <CheckCircleOutlineIcon />
                    </Avatar>
                    <Box>
                      <Typography fontWeight={800}>Regla de tipo</Typography>
                      <Typography variant="body2" sx={{ color: "#64748b" }}>
                        Un producto solo puede tener un tipo de variante.
                      </Typography>
                    </Box>
                  </Stack>

                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Chip
                      label={tipoPermitido ? `Tipo permitido: ${tipoPermitido}` : "Aún no definido"}
                      color={tipoPermitido ? "primary" : "default"}
                      sx={{ borderRadius: 2, fontWeight: 700 }}
                    />
                    {variantesExistentes.length > 0 && (
                      <Chip
                        label={`${variantesExistentes.length} existentes`}
                        sx={{ borderRadius: 2, fontWeight: 700 }}
                      />
                    )}
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {producto && (
              <>
                <Grid container spacing={2.5} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={8}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2.2,
                        borderRadius: 4,
                        borderColor: "#dbe6f3",
                        bgcolor: "#ffffff",
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                        <Avatar sx={{ bgcolor: "#e0f2fe", color: "#0369a1" }}>
                          <Inventory2OutlinedIcon />
                        </Avatar>
                        <Box>
                          <Typography fontWeight={800}>Producto seleccionado</Typography>
                          <Typography variant="body2" sx={{ color: "#64748b" }}>
                            Verifica la información antes de crear las variantes.
                          </Typography>
                        </Box>
                      </Stack>

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <TextField label="Código" value={producto.code || ""} disabled fullWidth />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField label="Marca" value={producto.brand || ""} disabled fullWidth />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField label="Precio base" value={Number(producto.pvp1 || 0).toFixed(2)} disabled fullWidth />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Card sx={{ borderRadius: 4, boxShadow: "none", border: "1px solid #dbe6f3", height: "100%" }}>
                      <CardContent sx={{ p: 2.2 }}>
                        <Typography fontWeight={800} sx={{ mb: 1.5 }}>
                          Resumen de la tanda
                        </Typography>
                        <Stack spacing={1.2}>
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography variant="body2" sx={{ color: "#64748b" }}>
                              Variantes nuevas
                            </Typography>
                            <Typography fontWeight={800}>{resumen.total}</Typography>
                          </Box>
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography variant="body2" sx={{ color: "#64748b" }}>
                              Precio promedio
                            </Typography>
                            <Typography fontWeight={800}>${resumen.promedio}</Typography>
                          </Box>
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography variant="body2" sx={{ color: "#64748b" }}>
                              Con imagen
                            </Typography>
                            <Typography fontWeight={800}>{resumen.conImagen}</Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {variantesExistentes.length > 0 && (
                  <Paper
                    sx={{
                      p: 2.2,
                      mb: 3,
                      borderRadius: 4,
                      border: "1px solid #fde68a",
                      bgcolor: "#fffbeb",
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                      <Avatar sx={{ bgcolor: "#fef3c7", color: "#b45309" }}>
                        <CategoryOutlinedIcon />
                      </Avatar>
                      <Box>
                        <Typography fontWeight={800}>Variantes ya registradas</Typography>
                        <Typography variant="body2" sx={{ color: "#92400e" }}>
                          Este producto ya tiene variantes guardadas. Usa el mismo tipo para mantener la estructura correcta.
                        </Typography>
                      </Box>
                    </Stack>

                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: "#1565C0" }}>
                          <TableCell sx={{ color: "#fff" }}>Tipo</TableCell>
                          <TableCell sx={{ color: "#fff" }}>Valor</TableCell>
                          <TableCell sx={{ color: "#fff" }}>Precio</TableCell>
                          <TableCell sx={{ color: "#fff" }}>IVA %</TableCell>
                          <TableCell sx={{ color: "#fff" }}>IVA $</TableCell>
                          <TableCell sx={{ color: "#fff" }}>Stock mín.</TableCell>
                          <TableCell sx={{ color: "#fff" }}>SKU</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {variantesExistentes.map((v) => (
                          <TableRow key={v.id}>
                            <TableCell>{v.variant_type}</TableCell>
                            <TableCell>{v.value}</TableCell>
                            <TableCell>${Number(v.price).toFixed(2)}</TableCell>
                            <TableCell>{v.iva_porcentaje === 999 ? "No objeto" : `${v.iva_porcentaje}%`}</TableCell>
                            <TableCell>{Number(v.iva_valor ?? 0).toFixed(2)}</TableCell>
                            <TableCell>{v.stock_minimo ?? "-"}</TableCell>
                            <TableCell>{v.sku}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                )}

                <Paper
                  sx={{
                    p: 2.5,
                    mb: 3,
                    borderRadius: 4,
                    border: "1px solid #dbe6f3",
                    bgcolor: "#ffffff",
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                    <Avatar sx={{ bgcolor: "#dbeafe", color: "#1565C0" }}>
                      <SellOutlinedIcon />
                    </Avatar>
                    <Box>
                      <Typography fontWeight={800}>Paso 2: agrega una variante</Typography>
                      <Typography variant="body2" sx={{ color: "#64748b" }}>
                        Completa solo los campos importantes. El IVA se calcula automáticamente.
                      </Typography>
                    </Box>
                  </Stack>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" sx={{ mb: 0.8, fontWeight: 700, color: "#334155" }}>
                        Tipo de variante
                      </Typography>
                      <Select value={tipo} onChange={(e) => setTipo(e.target.value)} displayEmpty fullWidth>
                        <MenuItem value="">
                          <em>Selecciona un tipo</em>
                        </MenuItem>
                        {TIPOS.map((t) => (
                          <MenuItem key={t} value={t} disabled={!!tipoPermitido && tipoPermitido !== t}>
                            {t}
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" sx={{ mb: 0.8, fontWeight: 700, color: "#334155" }}>
                        Valor
                      </Typography>
                      <TextField
                        placeholder="Ej. Rojo, 256 GB, Grande"
                        value={valor}
                        onChange={(e) => setValor(e.target.value)}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <Typography variant="body2" sx={{ mb: 0.8, fontWeight: 700, color: "#334155" }}>
                        Precio
                      </Typography>
                      <TextField
                        type="number"
                        placeholder="0.00"
                        value={precio}
                        onChange={(e) => setPrecio(e.target.value)}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <Typography variant="body2" sx={{ mb: 0.8, fontWeight: 700, color: "#334155" }}>
                        IVA
                      </Typography>
                      <Select value={ivaPorc} onChange={(e) => setIvaPorc(e.target.value)} fullWidth>
                        {IVA_OPTIONS.map((o) => (
                          <MenuItem key={o} value={o}>
                            {o === 999 ? "No objeto de IVA" : `${o}%`}
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <Typography variant="body2" sx={{ mb: 0.8, fontWeight: 700, color: "#334155" }}>
                        IVA calculado
                      </Typography>
                      <TextField value={ivaValorPreview} fullWidth InputProps={{ readOnly: true }} />
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" sx={{ mb: 0.8, fontWeight: 700, color: "#334155" }}>
                        Stock mínimo
                      </Typography>
                      <TextField
                        type="number"
                        placeholder="Opcional"
                        value={stockMin}
                        onChange={(e) => setStockMin(e.target.value)}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} md={5}>
                      <Typography variant="body2" sx={{ mb: 0.8, fontWeight: 700, color: "#334155" }}>
                        Imagen
                      </Typography>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
                        <Button
                          component="label"
                          variant="outlined"
                          startIcon={<ImageIcon />}
                          sx={{ borderRadius: 3, textTransform: "none", minWidth: 170 }}
                        >
                          Subir imagen
                          <input type="file" hidden accept="image/*" onChange={(e) => setImagen(e.target.files[0])} />
                        </Button>

                        <Box
                          sx={{
                            width: 68,
                            height: 68,
                            borderRadius: 3,
                            border: "1px dashed #cbd5e1",
                            bgcolor: "#f8fafc",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                            flexShrink: 0,
                          }}
                        >
                          {imagen ? (
                            <Box
                              component="img"
                              src={URL.createObjectURL(imagen)}
                              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                          ) : (
                            <ImageIcon sx={{ color: "#94a3b8" }} />
                          )}
                        </Box>

                        <Typography variant="body2" sx={{ color: "#64748b" }}>
                          {imagen ? imagen.name : "Puedes dejarlo vacío si no deseas subir imagen"}
                        </Typography>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ mb: 0.8, fontWeight: 700, color: "#334155" }}>
                        Acción
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={addVariant}
                        fullWidth
                        sx={{
                          bgcolor: "#1565C0",
                          borderRadius: 3,
                          py: 1.5,
                          textTransform: "none",
                          fontWeight: 800,
                          "&:hover": { bgcolor: "#0D47A1" },
                        }}
                      >
                        Añadir a la lista
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>

                {variantes.length > 0 && (
                  <Paper
                    sx={{
                      p: 2.5,
                      borderRadius: 4,
                      border: "1px solid #dbe6f3",
                      bgcolor: "#ffffff",
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", md: "center" }}
                      spacing={1.5}
                      sx={{ mb: 2 }}
                    >
                      <Box>
                        <Typography variant="h6" fontWeight={800} sx={{ color: "#1565C0" }}>
                          Paso 3: revisa y guarda
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#64748b" }}>
                          Aquí ves todas las variantes nuevas antes de enviarlas al servidor.
                        </Typography>
                      </Box>

                      <Chip
                        label={`${variantes.length} variante${variantes.length !== 1 ? "s" : ""} lista${variantes.length !== 1 ? "s" : ""}`}
                        color="primary"
                        sx={{ borderRadius: 2, fontWeight: 800 }}
                      />
                    </Stack>

                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: "#1565C0" }}>
                          {["Tipo", "Valor", "Precio", "IVA %", "IVA $", "Stock mín.", "Imagen", "Eliminar"].map((h) => (
                            <TableCell key={h} sx={{ color: "#fff", fontWeight: 800 }}>
                              {h}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {variantes.map((v) => (
                          <TableRow key={v.id} hover>
                            <TableCell>{v.tipo}</TableCell>
                            <TableCell>{v.valor}</TableCell>
                            <TableCell>${v.precio.toFixed(2)}</TableCell>
                            <TableCell>{v.iva_porcentaje === 999 ? "No objeto" : `${v.iva_porcentaje}%`}</TableCell>
                            <TableCell>${v.iva_valor.toFixed(2)}</TableCell>
                            <TableCell>{v.stock_minimo ?? "-"}</TableCell>
                            <TableCell>
                              {v.imagen ? (
                                <Box
                                  component="img"
                                  src={URL.createObjectURL(v.imagen)}
                                  sx={{ width: 52, height: 52, borderRadius: 2, objectFit: "cover", border: "1px solid #e2e8f0" }}
                                />
                              ) : (
                                "–"
                              )}
                            </TableCell>
                            <TableCell>
                              <Tooltip title="Quitar de la lista">
                                <IconButton onClick={() => deleteVariant(v.id)}>
                                  <DeleteIcon color="error" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    <Divider sx={{ my: 2.5 }} />

                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, flexWrap: "wrap" }}>
                      <Button
                        variant="outlined"
                        onClick={() => setVariantes([])}
                        sx={{ borderRadius: 3, textTransform: "none", fontWeight: 700 }}
                      >
                        Limpiar lista
                      </Button>
                      <Button
                        variant="contained"
                        onClick={saveAll}
                        disabled={saving}
                        sx={{
                          bgcolor: "#1565C0",
                          borderRadius: 3,
                          px: 3,
                          textTransform: "none",
                          fontWeight: 800,
                          "&:hover": { bgcolor: "#0D47A1" },
                        }}
                      >
                        {saving ? "Guardando..." : "Guardar variantes"}
                      </Button>
                    </Box>
                  </Paper>
                )}
              </>
            )}
          </Paper>
        </Box>
      </Box>

      <Snackbar
        open={!!error}
        autoHideDuration={3500}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="error" sx={{ borderRadius: 3 }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={2500}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success" sx={{ borderRadius: 3 }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}